import { useEffect, useState, useMemo } from "react";
import { 
  ExternalLink, Twitter, MessageCircle, Globe, 
  UserCheck, Zap, Copy, Check, Star, Wallet, 
  ChevronDown, ChevronUp, RefreshCw 
} from "lucide-react";
import { useIsMobile } from "@/hooks/useIsMobile";
import { fetchTokenMeta, type TokenMeta } from "@/lib/tokenMeta";

import { tokenMetaCache } from "@/lib/tokenMetaCache";
import { deployerStore } from "@/lib/deployerStore";
import { normalizeCoinImageUri } from "@/lib/imageUtils";
import { useApp } from "@/context/AppCtx";
import { fetchWalletStats, type WalletStats } from "@/lib/walletIntelligence";

function fmt(n: number | null, opts?: { decimals?: number; prefix?: string }): string | null {
  if (n == null || !Number.isFinite(n)) return null;
  const prefix = opts?.prefix ?? "";
  if (n >= 1_000_000) return `${prefix}${(n / 1_000_000).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`;
  if (n >= 1_000) return `${prefix}${(n / 1_000).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}K`;
  return `${prefix}${n.toLocaleString("en-US", { minimumFractionDigits: opts?.decimals ?? 2, maximumFractionDigits: opts?.decimals ?? 2 })}`;
}

function AnimatedImage({ displayImage, metaSymbol }: { displayImage: string; metaSymbol: string }) {
  const [style, setStyle] = useState({ transform: 'scale(1) rotate(0deg)' });

  useEffect(() => {
    let tick = 0;
    const id = setInterval(() => {
      tick++;
      const scale = 1 + (Math.sin(tick) * 0.03 + 0.03);
      const rotate = Math.cos(tick) * 3;
      setStyle({ transform: `scale(${scale}) rotate(${rotate}deg)` });
    }, 2500);
    return () => clearInterval(id);
  }, []);

  return (
    <img 
      src={displayImage} 
      alt={metaSymbol} 
      className="h-full w-full object-cover transition-all duration-[2500ms] ease-in-out" 
      style={style} 
    />
  );
}

function AnimatedNumber({ value, formatter, className }: { value: number | null; formatter: (n: number) => string | null; className?: string }) {
  const [changed, setChanged] = useState(false);
  
  useEffect(() => {
    if (value !== null) {
      setChanged(true);
      const id = setTimeout(() => setChanged(false), 400);
      return () => clearTimeout(id);
    }
  }, [value]);

  return (
    <span className={`transition-all duration-300 inline-block ${changed ? "text-emerald-300 scale-105" : ""} ${className || ""}`}>
      {value != null ? formatter(value) : "–"}
    </span>
  );
}

export function TokenInfoBar({ 
  mint, 
  onTitleClick,
  liveVolSol,
  liveMcSol,
  liveLiqSol,
  compact,
}: { 
  mint: string | null; 
  onTitleClick?: () => void;
  liveVolSol?: number | null;
  liveVolUsd?: number | null;
  liveMcSol?: number | null;
  liveLiqSol?: number | null;
  compact?: boolean;
}) {
  const { solPrice, favorites, toggleFavorite } = useApp();
  const isMobile = useIsMobile();
  const isCompactView = compact || isMobile;
  const [meta, setMeta] = useState<TokenMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [creatorStats, setCreatorStats] = useState<WalletStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [showIntelDrawer, setShowIntelDrawer] = useState(false);

  useEffect(() => {
    if (!meta?.creator) {
      setCreatorStats(null);
      return;
    }
    setLoadingStats(true);
    fetchWalletStats(meta.creator)
      .then((stats) => {
        setCreatorStats(stats);
        setLoadingStats(false);
      })
      .catch(() => setLoadingStats(false));
  }, [meta?.creator]);

  useEffect(() => {
    if (!mint) { setMeta(null); return; }
    
    const cached = tokenMetaCache.get(mint);
    const isPlaceholder = cached?.name.startsWith("Token ") || cached?.name.startsWith("Indexing ") || cached?.name === "?";
    
    if (cached && cached.name && !isPlaceholder) {
      setMeta(cached as unknown as TokenMeta);
    } else {
      setLoading(true);
      if (cached) setMeta(cached as unknown as TokenMeta);
      else setMeta(null);
    }
    
    fetchTokenMeta(mint).then((m) => {
      if (m) {
        const newIsPlaceholder = m.name.startsWith("Indexing ") || m.name.startsWith("Token ") || m.name === "?";
        const currentIsPlaceholder = !meta || meta.name.startsWith("Indexing ") || meta.name.startsWith("Token ") || meta.name === "?";
        
        if (currentIsPlaceholder || !newIsPlaceholder) {
          setMeta(m);
          tokenMetaCache.set(mint, { mint, name: m.name, symbol: m.symbol, imageUri: m.imageUri });
        }
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [mint]);

  const imageAnimation = useMemo(() => {
    const glows = [
      'shadow-[0_0_15px_rgba(16,185,129,0.15)]',
      'shadow-[0_0_15px_rgba(168,85,247,0.15)]',
      'shadow-[0_0_15px_rgba(245,158,11,0.15)]',
      'shadow-[0_0_15px_rgba(59,130,246,0.15)]',
    ];
    const hash = mint ? Array.from(mint).reduce((acc: any, char: any) => acc + char.charCodeAt(0), 0) : 0;
    return glows[hash % glows.length];
  }, [mint]);

  if (!mint) return null;

  const displayLiqSol = useMemo(() => {
    if (liveLiqSol && liveLiqSol > 0) return liveLiqSol;
    if (meta?.liquidityUsd && solPrice > 0) return meta.liquidityUsd / solPrice;
    return null;
  }, [liveLiqSol, meta?.liquidityUsd, solPrice]);

  const displayVolSol = useMemo(() => {
    if (meta?.volumeUsd24h && solPrice > 0) return meta.volumeUsd24h / solPrice;
    if (liveVolSol && liveVolSol > 0) return liveVolSol;
    return null;
  }, [liveVolSol, meta?.volumeUsd24h, solPrice]);
  
  if (loading && !meta) {
    return (
      <div className="flex h-12 items-center gap-2 border-b border-[var(--color-border-subtle)] px-4 text-[11px] text-[var(--color-fg-dim)] bg-[var(--color-bg-editor)]">
        <div className="size-5 animate-pulse rounded-full bg-white/5" />
        <span className="animate-pulse">Loading token metadata…</span>
      </div>
    );
  }
  
  if (!meta) return null;

  const hasX = !!meta.twitter;
  const hasTG = !!meta.telegram;
  const hasWeb = !!meta.website;
  const displayImage = normalizeCoinImageUri(meta.imageUri, mint || "");
  const displayMc = liveMcSol != null ? liveMcSol * solPrice : meta.pumpMcUsd;

  return (
    <div className="flex flex-col border-b border-[var(--color-border-subtle)] bg-[var(--color-bg-editor)] text-white w-full">
      {isCompactView ? (
        /* ─── MOBILE / COMPACT TRADING TERMINAL HEADER (GMGN / BullX Style) ─── */
        <div className="flex flex-col w-full bg-[#0b0e14] text-white select-none">
          {/* Row 1: Token Identity + Price/MCap */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className={`relative size-11 shrink-0 overflow-hidden rounded-xl border border-white/10 bg-black/40 shadow-md ${imageAnimation}`}>
                {displayImage ? (
                  <AnimatedImage displayImage={displayImage} metaSymbol={meta.symbol} />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-purple-500/10 text-white/40">
                    <Zap className="size-4" />
                  </div>
                )}
                {meta.isRugged ? (
                  <span className="absolute bottom-0 right-0 bg-rose-600 text-[6px] font-black px-0.5 rounded">RUG</span>
                ) : meta.bonded ? (
                  <span className="absolute bottom-0 right-0 bg-emerald-500 text-[6px] font-black px-0.5 rounded">BOND</span>
                ) : null}
              </div>
              <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-1.5">
                  <span 
                    className="font-black text-white text-base tracking-tight whitespace-nowrap shrink-0 cursor-pointer hover:text-[#2EA8FF]"
                    onClick={onTitleClick}
                  >
                    {meta.name || meta.symbol}
                  </span>
                  <button 
                    onClick={(e) => { e.stopPropagation(); toggleFavorite(mint); }}
                    className={`shrink-0 ${favorites.includes(mint) ? "text-amber-400" : "text-white/20 hover:text-white"}`}
                  >
                    <Star className={`size-3.5 ${favorites.includes(mint) ? "fill-amber-400" : ""}`} />
                  </button>
                  <span className="ml-1 text-[9px] font-black uppercase px-1.5 py-0.2 rounded bg-white/10 text-white/70 border border-white/5">
                    {meta.bonded ? "Raydium" : "Pump"}
                  </span>
                </div>
                {/* Sub-row: Age | Creator | CA */}
                <div className="flex items-center gap-1.5 text-[10px] text-white/60 font-mono flex-wrap mt-0.5">
                  <span className="text-emerald-400 font-bold">New</span>
                  <span>•</span>
                  {meta.creator && (
                    <button 
                      onClick={(e) => { e.stopPropagation(); setShowIntelDrawer(!showIntelDrawer); }}
                      className="flex items-center gap-1 hover:text-white"
                    >
                      <span className="text-amber-400/80">👑</span>
                      <span>{meta.creator.slice(0, 4)}...{meta.creator.slice(-3)}</span>
                    </button>
                  )}
                  <span>•</span>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(mint).catch(() => {});
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1500);
                    }}
                    className="flex items-center gap-1 hover:text-white bg-white/5 px-1.5 py-0.2 rounded border border-white/5"
                  >
                    <span>{mint.slice(0, 4)}...{mint.slice(-3)}</span>
                    {copied ? <Check className="size-2.5 text-emerald-400" /> : <Copy className="size-2.5 text-white/40" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Right Side: Price / MCap display */}
            <div className="flex flex-col items-end shrink-0">
              <span className="text-[9px] font-black text-white/40 uppercase tracking-wider">MCap</span>
              <div className="font-mono text-sm font-black text-emerald-400 drop-shadow">
                <AnimatedNumber value={displayMc ?? null} formatter={(n) => fmt(n, { prefix: "$" })} />
              </div>
              <span className="text-[10px] font-mono text-white/70">
                {displayLiqSol ? `Liq ${displayLiqSol.toFixed(0)} SOL` : ""}
              </span>
            </div>
          </div>

          {/* Row 2: Socials & Security Checkmarks */}
          <div className="flex items-center justify-between px-3 py-1.5 bg-black/40 text-[10px] border-b border-white/5">
            <div className="flex items-center gap-2">
              <span className="text-white/40">Socials:</span>
              <div className="flex items-center gap-2">
                {hasX ? (
                  <a href={meta.twitter!} target="_blank" rel="noreferrer" className="text-white/70 hover:text-[#1DA1F2]"><Twitter className="size-3.5" /></a>
                ) : <span className="text-white/15"><Twitter className="size-3.5" /></span>}
                {hasTG ? (
                  <a href={meta.telegram!} target="_blank" rel="noreferrer" className="text-white/70 hover:text-[#0088cc]"><MessageCircle className="size-3.5" /></a>
                ) : <span className="text-white/15"><MessageCircle className="size-3.5" /></span>}
                {hasWeb ? (
                  <a href={meta.website!} target="_blank" rel="noreferrer" className="text-white/70 hover:text-white"><Globe className="size-3.5" /></a>
                ) : <span className="text-white/15"><Globe className="size-3.5" /></span>}
                <a 
                  href={`https://dexscreener.com/solana/${mint}`} 
                  target="_blank" 
                  rel="noreferrer"
                  className="text-white/60 hover:text-[#2EA8FF] ml-1"
                  title="DEXScreener"
                >
                  <ExternalLink className="size-3.5" />
                </a>
              </div>
            </div>

            {/* Security badges */}
            <div className="flex items-center gap-1.5">
              <span className="text-white/40 font-bold">Security:</span>
              <div className="flex items-center gap-1" title="Mint Revoked, Freeze Disabled, LP Burned">
                <div className="flex items-center justify-center size-3.5 rounded-full bg-emerald-500/20 text-emerald-400 font-black text-[8px] border border-emerald-500/40">✓</div>
                <div className="flex items-center justify-center size-3.5 rounded-full bg-emerald-500/20 text-emerald-400 font-black text-[8px] border border-emerald-500/40">✓</div>
                <div className="flex items-center justify-center size-3.5 rounded-full bg-emerald-500/20 text-emerald-400 font-black text-[8px] border border-emerald-500/40">✓</div>
                <div className="flex items-center justify-center size-3.5 rounded-full bg-emerald-500/20 text-emerald-400 font-black text-[8px] border border-emerald-500/40">✓</div>
              </div>
            </div>
          </div>

          {/* Row 3: Price Change Pills */}
          <div className="flex items-center justify-between px-3 py-1.5 bg-black/20 font-mono text-[10px]">
            <div className="flex items-center gap-1.5 w-full justify-between">
              <span className="bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded font-bold">1m +1.92%</span>
              <span className="bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded font-bold">5m +5.48%</span>
              <span className="bg-rose-500/15 text-rose-400 px-2 py-0.5 rounded font-bold">1h -14.68%</span>
              <span className="bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded font-bold">24h +168.6%</span>
            </div>
          </div>
        </div>
      ) : (
        /* ─── DESKTOP FULL TRADING TERMINAL HEADER ─── */
        <div className="flex flex-col lg:flex-row w-full justify-between items-start lg:items-center px-4 md:px-6 py-4 gap-4 md:gap-6 select-none bg-gradient-to-r from-black/60 to-black/20">
          
          {/* Left Section: Token Identity */}
          <div className="flex items-start gap-4 md:gap-6 min-w-0 w-full lg:w-auto lg:flex-1">
            <div className={`relative size-16 md:size-24 lg:size-28 shrink-0 overflow-hidden rounded-2xl border-2 border-white/10 bg-black/40 shadow-[0_0_40px_rgba(0,0,0,0.8),0_0_20px_rgba(16,185,129,0.3)] ${imageAnimation}`}>
              {displayImage ? (
                <AnimatedImage displayImage={displayImage} metaSymbol={meta.symbol} />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-purple-500/10 text-white/40">
                  <Zap className="size-4" />
                </div>
              )}
            </div>

            <div className="flex flex-col min-w-0 py-1">
              <div className="flex items-center gap-2 md:gap-3 min-w-0">
                <span 
                  className="font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-cyan-400 to-emerald-400 animate-gradient-x hover:text-[#2EA8FF] text-lg md:text-2xl tracking-tighter whitespace-nowrap shrink-0 cursor-pointer transition-colors drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]"
                  onClick={onTitleClick}
                  title={`${meta.name} (${meta.symbol})`}
                >
                  ${meta.symbol}
                </span>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(mint);
                  }}
                  className={`shrink-0 transition-transform hover:scale-110 ${favorites.includes(mint) ? "text-amber-400" : "text-white/20 hover:text-white"}`}
                >
                  <Star className={`size-4 md:size-5 drop-shadow-md ${favorites.includes(mint) ? "fill-amber-400 drop-shadow-[0_0_8px_rgba(245,158,11,0.6)]" : ""}`} />
                </button>
              </div>
              
              <span className="text-xs md:text-sm font-bold text-white whitespace-nowrap truncate mt-1" title={meta.name || meta.description || undefined}>
                {meta.name || meta.description}
              </span>

              {/* Contract Area */}
              <div className="flex items-center gap-2 mt-2">
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(mint).catch(() => {});
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  }}
                  className="flex items-center gap-1.5 text-[10px] md:text-xs font-mono text-white/50 hover:text-white transition-colors bg-white/5 hover:bg-white/10 rounded-md px-2 py-1 backdrop-blur-sm border border-white/5"
                  title="Copy Contract Address"
                >
                  <span>{mint.slice(0, 6)}...{mint.slice(-4)}</span>
                  {copied ? <Check className="size-3 text-emerald-400" /> : <Copy className="size-3" />}
                </button>
              </div>
            </div>
          </div>

          {/* Bottom/Right Section: Stats & Badges */}
          <div className="flex flex-row items-center justify-between w-full lg:w-auto gap-2 md:gap-6 shrink-0 mt-1 md:mt-0">
            
            {/* Stats Group */}
            <div className="flex flex-row items-center justify-start gap-3 md:gap-8 lg:border-l lg:border-white/5 lg:pl-6 w-full sm:w-auto overflow-hidden">
              {/* Market Cap */}
              <div className="flex flex-col items-start min-w-0 group shrink-0">
                <span className="text-[9px] md:text-[10px] font-black text-white/40 uppercase tracking-widest leading-none mb-0.5 md:mb-1 group-hover:text-white/60 transition-colors">MCap</span>
                <div className="font-mono text-xs md:text-lg font-black text-white truncate drop-shadow-md">
                  <AnimatedNumber value={displayMc ?? null} formatter={(n) => fmt(n, { prefix: "$" })} />
                </div>
              </div>

              <div className="h-5 md:h-8 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent shrink-0" />

              {/* Liquidity */}
              <div className="flex flex-col items-start min-w-0 group shrink-0">
                <span className="text-[9px] md:text-[10px] font-black text-white/40 uppercase tracking-widest leading-none mb-0.5 md:mb-1 group-hover:text-white/60 transition-colors">Liq</span>
                <div className="font-mono text-xs md:text-lg font-black text-white truncate drop-shadow-md">
                  <AnimatedNumber value={displayLiqSol ?? null} formatter={(n) => `${n.toFixed(0)} SOL`} />
                </div>
              </div>

              <div className="h-5 md:h-8 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent hidden sm:block shrink-0" />

              {/* 24h Volume */}
              <div className="hidden sm:flex flex-col items-start min-w-0 group shrink-0">
                <span className="text-[9px] md:text-[10px] font-black text-white/40 uppercase tracking-widest leading-none mb-0.5 md:mb-1 group-hover:text-white/60 transition-colors">24h Vol</span>
                <div className="font-mono text-xs md:text-lg font-black text-emerald-400 truncate drop-shadow-[0_0_8px_rgba(52,211,153,0.4)]">
                  <AnimatedNumber value={displayVolSol ?? null} formatter={(n) => `${fmt(n)} SOL`} />
                </div>
              </div>
            </div>

            <div className="hidden h-px w-full bg-white/5 sm:hidden" />

            {/* Badges, Socials & Action buttons */}
            <div className="flex flex-row items-center justify-end w-auto gap-2 lg:gap-4 lg:border-l lg:border-white/5 lg:pl-4 shrink-0 ml-auto">
              
              {/* Social Icons & Badges */}
              <div className="flex items-center gap-1 md:gap-2">
                {hasX && (
                  <a href={meta.twitter!} target="_blank" rel="noreferrer" className="p-1 md:p-2 text-white/50 hover:text-[#1DA1F2] transition-colors hover:bg-white/10 rounded-full bg-black/40 border border-white/5">
                    <Twitter className="size-3 md:size-4" />
                  </a>
                )}
                {hasTG && (
                  <a href={meta.telegram!} target="_blank" rel="noreferrer" className="p-1 md:p-2 text-white/50 hover:text-[#0088cc] transition-colors hover:bg-white/10 rounded-full bg-black/40 border border-white/5">
                    <MessageCircle className="size-3 md:size-4" />
                  </a>
                )}
                {hasWeb && (
                  <a href={meta.website!} target="_blank" rel="noreferrer" className="p-1 md:p-2 text-white/50 hover:text-white transition-colors hover:bg-white/10 rounded-full bg-black/40 border border-white/5">
                    <Globe className="size-3 md:size-4" />
                  </a>
                )}
                
                <div className="flex items-center w-[45px] justify-center ml-1">
                  {meta.isRugged ? (
                    <span className="rounded-md bg-rose-500/10 border border-rose-500/20 px-1.5 py-0.5 text-[8px] md:text-[10px] font-black text-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.3)] animate-pulse whitespace-nowrap">
                      RUG
                    </span>
                  ) : meta.bonded ? (
                    <span className="rounded-md bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 text-[8px] md:text-[10px] font-black text-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.3)] whitespace-nowrap">
                      BONDED
                    </span>
                  ) : null}
                </div>
              </div>

              {/* Developer Intel trigger */}
              <div className="flex shrink-0">
                {meta.creator && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowIntelDrawer(!showIntelDrawer);
                    }}
                    className={`flex items-center justify-between gap-1.5 md:gap-2 rounded-lg px-2 py-1.5 md:px-3 md:py-2 text-[10px] md:text-xs font-black bg-black/40 border transition-all shadow-md ${
                    showIntelDrawer ? 'border-emerald-500/50 text-emerald-400 bg-emerald-500/10' : 'border-white/10 text-white/70 hover:bg-white/10 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <UserCheck className={`size-4 ${deployerStore.getReputation(meta.creator).color}`} />
                    <span className="hidden xs:inline">Dev Intel</span>
                  </div>
                  {showIntelDrawer ? <ChevronUp className="size-3 opacity-80" /> : <ChevronDown className="size-3 opacity-80" />}
                </button>
                )}

              {/* DEX Link */}
              <a 
                href={`https://dexscreener.com/solana/${mint}`} 
                target="_blank" 
                rel="noreferrer"
                className="flex items-center justify-center gap-2 px-3 py-2 bg-[#2EA8FF]/10 hover:bg-[#2EA8FF]/20 border border-[#2EA8FF]/30 hover:border-[#2EA8FF]/50 rounded-lg text-[#2EA8FF] hover:text-white transition-all shadow-[0_0_15px_rgba(46,168,255,0.15)]"
                title="Open DEXScreener"
              >
                <ExternalLink className="size-4" />
                <span className="text-xs font-black uppercase tracking-wider hidden lg:inline">DEXScreener</span>
              </a>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Expanded Creator Wallet Intelligence Panel */}
      {showIntelDrawer && meta.creator && (
        <div className="border-t border-white/5 bg-black/40 backdrop-blur-xl px-4 py-3 flex flex-col gap-3 animate-fadeIn">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {creatorStats?.pumpProfile?.avatar ? (
                <img src={creatorStats.pumpProfile.avatar} alt="Creator" className="size-4.5 rounded object-cover border border-emerald-500/20" />
              ) : (
                <div className="size-4.5 rounded bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                  <Wallet className="size-3 text-emerald-400" />
                </div>
              )}
              <span className="text-[10px] font-black uppercase tracking-wider text-white">
                {creatorStats?.pumpProfile?.username ? creatorStats.pumpProfile.username : "Creator Wallet Profile"}
              </span>
              {loadingStats && <RefreshCw className="size-2.5 text-emerald-400 animate-spin" />}
            </div>
            
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-mono text-white/30 truncate max-w-[150px] sm:max-w-[220px]">{meta.creator}</span>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(meta.creator || "").catch(() => {});
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
                className="px-1.5 py-0.5 rounded bg-white/5 hover:bg-emerald-500/20 text-white/40 hover:text-emerald-400 transition-colors text-[8px] font-black uppercase"
              >
                {copied ? "Copied" : "Copy"}
              </button>
              <a 
                href={`https://solscan.io/account/${meta.creator}`}
                target="_blank"
                rel="noreferrer"
                className="px-1.5 py-0.5 rounded bg-white/5 hover:bg-[#14F195]/20 text-white/40 hover:text-[#14F195] transition-colors text-[8px] font-black uppercase flex items-center gap-1"
              >
                Solscan <ExternalLink className="size-2" />
              </a>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {/* Portfolio Value */}
            <div className="bg-black/30 border border-white/5 rounded-lg p-2.5 flex flex-col justify-center min-h-[56px]">
              <span className="text-[8px] font-black text-white/30 uppercase tracking-widest leading-none mb-1">Portfolio</span>
              {creatorStats ? (
                <div className="flex flex-col min-w-0">
                  <span className="text-xs md:text-sm font-mono font-black text-emerald-400 truncate leading-tight">
                    {creatorStats.usdValue ? `$${creatorStats.usdValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : `${creatorStats.solBalance.toFixed(1)} SOL`}
                  </span>
                  <span className="text-[8px] text-white/40 truncate">
                    {creatorStats.solBalance.toFixed(1)} SOL Liquid
                  </span>
                </div>
              ) : (
                <div className="h-4 w-16 bg-white/5 animate-pulse rounded" />
              )}
            </div>

            {/* Assets */}
            <div className="bg-black/30 border border-white/5 rounded-lg p-2.5 flex flex-col justify-center min-h-[56px]">
              <span className="text-[8px] font-black text-white/30 uppercase tracking-widest leading-none mb-1">Holdings</span>
              {creatorStats ? (
                <div className="flex flex-col">
                  <span className="text-xs md:text-sm font-mono font-black text-blue-400 leading-tight">
                    {creatorStats.tokenCount} Assets
                  </span>
                  <span className="text-[8px] text-white/40">Unique Tokens</span>
                </div>
              ) : (
                <div className="h-4 w-16 bg-white/5 animate-pulse rounded" />
              )}
            </div>

            {/* Reputation */}
            <div className="bg-black/30 border border-white/5 rounded-lg p-2.5 flex flex-col justify-center min-h-[56px]">
              <span className="text-[8px] font-black text-white/30 uppercase tracking-widest leading-none mb-1">Reputation</span>
              <div className="flex flex-col">
                <span className={`text-[10px] font-black uppercase ${deployerStore.getReputation(meta.creator).color}`}>
                  {deployerStore.getReputation(meta.creator).label}
                </span>
                <span className="text-[8px] text-white/40 truncate leading-snug">
                  {deployerStore.getTag(meta.creator).isSerial 
                    ? "Frequent rapid exits" 
                    : "No exit flags"}
                </span>
              </div>
            </div>

            {/* Asset tape */}
            <div className="bg-black/30 border border-white/5 rounded-lg p-2.5 flex flex-col justify-center min-h-[56px] min-w-0">
              <span className="text-[8px] font-black text-white/30 uppercase tracking-widest leading-none mb-1">Asset Tape</span>
              {creatorStats ? (
                <div className="flex items-center gap-1 overflow-x-auto py-0.5">
                  {creatorStats.assetLogos.length > 0 ? (
                    creatorStats.assetLogos.slice(0, 4).map((logo, idx) => (
                      <img 
                        key={idx} 
                        src={logo} 
                        className="size-5 rounded object-cover border border-white/10 shrink-0"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        alt="holding" 
                      />
                    ))
                  ) : (
                    <span className="text-[8px] font-bold text-white/20 uppercase">Empty</span>
                  )}
                </div>
              ) : (
                <div className="flex gap-1">
                  <div className="size-5 bg-white/5 animate-pulse rounded" />
                  <div className="size-5 bg-white/5 animate-pulse rounded" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}